"use client";

import { useEffect, useState } from "react";

const COOKIE_CONSENT_KEY = "coplane_cookie_consent";

export function useCookieConsent() {
  const [hasConsent, setHasConsent] = useState<boolean | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    setHasConsent(stored === "false" ? false : true); 
  }, []);

  const acceptCookies = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    setHasConsent(true);
  };

  const declineCookies = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "false");
    setHasConsent(false);
  };

  return { hasConsent, acceptCookies, declineCookies };
}

export function CookieConsent() {
  const { acceptCookies, declineCookies } = useCookieConsent();
  const [showBanner, setShowBanner] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Only show icon if consent hasn't been explicitly set
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored === null) {
      setShowBanner(true);
    }
  }, []);

  const handleAccept = () => {
    acceptCookies();
    setShowBanner(false);
    setIsExpanded(false);
  };

  const handleDecline = () => {
    declineCookies();
    setShowBanner(false);
    setIsExpanded(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Icon Button - Bottom Left, above z-20 rails but below z-60 header */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="fixed bottom-4 left-4 z-[25] rounded-full bg-black/80 p-2 text-white shadow-lg backdrop-blur-sm transition-all hover:bg-black dark:bg-white/80 dark:text-black dark:hover:bg-white"
          aria-label="Cookie preferences"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
            <path d="M8.5 8.5v.01" />
            <path d="M16 15.5v.01" />
            <path d="M12 12v.01" />
            <path d="M11 17v.01" />
            <path d="M7 14v.01" />
          </svg>
        </button>
      )}

      {/* Expanded Banner */}
      {isExpanded && (
        <div className="fixed bottom-4 left-4 right-4 z-[25] md:left-auto md:right-4 md:max-w-md">
          <div className="rounded-lg border border-black/10 bg-white p-4 shadow-lg backdrop-blur-sm dark:border-white/10 dark:bg-black/90">
            <p className="mb-4 text-sm text-black/80 dark:text-white/80">
              This website uses cookies and similar technologies to collect information you provide and information about your interactions with our sites to improve your experience, analyze performance and traffic on our website and assist our marketing efforts and customer service. By continuing to browse, you agree to our use of these tools in accordance with our{' '}
              <a href="/legal/privacy-policy" className="underline hover:text-black/60 dark:hover:text-white/60">
                Privacy Policy
              </a>.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleAccept}
                className="flex-1 rounded-full bg-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-black/80 dark:bg-white dark:text-black dark:hover:bg-white/90"
              >
                Accept
              </button>
              <button
                onClick={handleDecline}
                className="flex-1 rounded-full border border-black/10 bg-transparent px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-black/5 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

